import React from 'react';
import { Box, Typography, Stack, Button, Divider } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  CoordinatePatient,
  DISPOSITION_LABELS,
  STATUS_CHIP_STYLE,
  URGENCY_CHIP_STYLE,
} from '~/types/coordinate';

const DEMO_TODAY = new Date('2026-05-01');

export type ActionKey = 'run_matching' | 'send_referral' | 'write_ehr' | 'contact_providers';

export interface NextAction {
  title: string;
  description: string;
  severity: 'info' | 'warning' | 'success';
  actionLabel?: string;
  actionKey?: ActionKey;
}

export function getNextAction(patient: CoordinatePatient): NextAction | null {
  const { coordinationStatus, providerOutreach, selectedProviderId, matchResults } = patient;
  const selectedName =
    matchResults?.find(m => m.provider.id === selectedProviderId)?.provider.name ??
    providerOutreach?.attempts.find(a => a.providerId === selectedProviderId)?.providerName;

  switch (coordinationStatus) {
    case 'pending':
      return {
        title: 'Ready to find providers',
        description: 'Set the care disposition and clinical needs on the left, then run matching to see available providers.',
        severity: 'info',
        actionLabel: 'Run Matching',
        actionKey: 'run_matching',
      };
    case 'matching':
      return {
        title: 'Matching in progress…',
        description: 'Coordinate AI is scanning provider availability based on this patient\'s clinical needs and insurance.',
        severity: 'info',
      };
    case 'matched':
      return providerOutreach
        ? {
            title: 'Provider responded — select one to confirm',
            description: 'AI outreach calls are complete. Select an available provider below and confirm to build the referral packet.',
            severity: 'info',
          }
        : {
            title: `${matchResults?.length ?? 0} providers matched`,
            description: 'Review the ranked list below and select a provider — or let Coordinate AI call the top 3 simultaneously to check availability.',
            severity: 'info',
            actionLabel: 'Contact Top 3 with AI',
            actionKey: 'contact_providers',
          };
    case 'confirming':
      return {
        title: 'Confirming availability…',
        description: 'Checking capacity with the selected provider. This takes a few seconds.',
        severity: 'info',
      };
    case 'confirmed':
      return {
        title: `Send referral to ${selectedName ?? 'provider'}`,
        description: 'Provider confirmed and referral packet assembled. Review the packet, then send.',
        severity: 'success',
        actionLabel: 'Send Referral',
        actionKey: 'send_referral',
      };
    case 'confirm_failed':
      return {
        title: 'Availability check failed — re-match needed',
        description: 'The selected provider is no longer available. Use the controls on the left to run matching and select another.',
        severity: 'warning',
        actionLabel: 'Run Matching Again',
        actionKey: 'run_matching',
      };
    case 'awaiting_response':
      return {
        title: `Waiting for response from ${selectedName ?? 'provider'}`,
        description: 'Referral sent. The provider typically responds within 1–4 hours. You\'ll be notified when they do.',
        severity: 'info',
      };
    case 'negotiating':
      return {
        title: 'Provider responded with conditions',
        description: 'Resolve all conditions in the negotiation thread below to confirm the placement.',
        severity: 'warning',
      };
    case 'accepted':
      return {
        title: 'Placement confirmed',
        description: `${selectedName ?? 'Provider'} accepted. Write the discharge planning note to the EHR to complete the handoff.`,
        severity: 'success',
        actionLabel: 'Write EHR Note',
        actionKey: 'write_ehr',
      };
    case 'declined':
      return {
        title: 'Referral declined — re-match needed',
        description: `${selectedName ?? 'Provider'} declined. Use the controls on the left to run matching and select a different provider.`,
        severity: 'warning',
        actionLabel: 'Run Matching Again',
        actionKey: 'run_matching',
      };
    default:
      return null;
  }
}

const ACTION_TINT = {
  info:    { bg: '#e8f6fb', border: '#b8e6f5', text: '#0b233f', btnBg: '#1565c0', btnHover: '#0d47a1' },
  success: { bg: '#e0f7f4', border: '#9de4da', text: '#005a4e', btnBg: '#00bda1', btnHover: '#009e87' },
  warning: { bg: '#fffbf2', border: '#fde8c0', text: '#bf360c', btnBg: '#e65100', btnHover: '#bf360c' },
};

function formatInsuranceType(type: string): string {
  return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

interface Props {
  patient: CoordinatePatient;
  onAction: (key: ActionKey) => void;
  onViewPacket: () => void;
}

const CasePageHeader: React.FC<Props> = ({ patient, onAction, onViewPacket }) => {
  const [m, d, y] = patient.admitDate.split('/').map(Number);
  const admitDate = new Date(y, m - 1, d);
  const daysSinceAdmit = Math.floor((DEMO_TODAY.getTime() - admitDate.getTime()) / 86_400_000);
  const losOver = daysSinceAdmit - patient.losTargetDays;

  // Match the patient-list pattern: deterministic floor/room from patient id
  const hash = Array.from(patient.id).reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const floor = (hash % 8) + 2;
  const room = 100 + (hash % 40);

  const statusStyle = STATUS_CHIP_STYLE[patient.coordinationStatus];
  const urgencyStyle = URGENCY_CHIP_STYLE[patient.urgency];
  const nextAction = getNextAction(patient);
  const tint = nextAction ? ACTION_TINT[nextAction.severity] : null;
  const isProcessing = patient.coordinationStatus === 'matching' || patient.coordinationStatus === 'confirming';

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        mb: 2,
        bgcolor: '#fff',
      }}
    >
      {/* Identity section */}
      <Box sx={{ px: 3, pt: 2, pb: 2.25 }}>
        {/* Name + status on one left-anchored row */}
        <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.25 }}>
            {patient.name}
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: statusStyle.color, lineHeight: 1.25 }}>
            {statusStyle.label}
          </Typography>
        </Stack>

        {/* Context: facility, care type, assignee */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Age {patient.age} · {patient.facilityName} Floor {floor} · Rm {room} · {DISPOSITION_LABELS[patient.disposition]}{patient.predictProgram ? ' via Predict' : ''} · {patient.assignedTo}
        </Typography>

        {/* Signals: urgency, LOS, admit date, insurance */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mt: 0.75 }}>
          <Box component="span" sx={{ color: urgencyStyle.color, fontWeight: 600 }}>
            {urgencyStyle.label}
          </Box>
          <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
            {(() => {
              const expected = new Date(admitDate);
              expected.setDate(expected.getDate() + patient.losTargetDays);
              return `Expected discharge ${expected.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })}`;
            })()}
          </Box>
          <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
          <Box component="span">Admit {patient.admitDate}</Box>
          <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
          <Box component="span">{formatInsuranceType(patient.insuranceType)} · {patient.memberId}</Box>
        </Typography>
      </Box>

      {/* Action section */}
      {nextAction && tint && (
        <>
          <Divider />
          <Box sx={{ px: 3, py: 2, bgcolor: tint.bg }}>
            <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap" useFlexGap>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: tint.text, mb: 0.25 }}>
                  {nextAction.title}
                </Typography>
                <Typography variant="body2" sx={{ color: tint.text, opacity: 0.8, fontSize: '0.82rem' }}>
                  {nextAction.description}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexShrink={0} flexWrap="wrap" useFlexGap>
                {patient.referralPacket && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
                    onClick={onViewPacket}
                    sx={{
                      fontSize: '0.78rem', fontWeight: 600,
                      color: tint.text,
                      borderColor: tint.border,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', borderColor: tint.text },
                    }}
                  >
                    View Referral Packet
                  </Button>
                )}
                {nextAction.actionLabel && nextAction.actionKey && (
                  <Button
                    variant="contained"
                    size="small"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 15 }} />}
                    onClick={() => onAction(nextAction.actionKey!)}
                    disabled={isProcessing}
                    sx={{
                      bgcolor: tint.btnBg,
                      '&:hover': { bgcolor: tint.btnHover },
                      fontWeight: 700,
                      fontSize: '0.82rem',
                    }}
                  >
                    {nextAction.actionLabel}
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CasePageHeader;
